var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { useTranslation } from 'react-i18next';
import globalStore from '../store';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { playAudio } from '../helper/audioHelper';
import { selectTool, bringManipulativesToFront } from '../helper/helper';
import { ColorEnum } from '../components/ColorPicker';
import '../styles/widgets/patternShapes.scss';
import { getImageSrc } from '../helper/imageBlobCache';
import { useDuplicateAnnounce } from '../helper/hooks/useDuplicateAnnounce';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
/* ------------------------------------------------------------------ */
/* CONSTANTS — imported from shared constants file */
/* ------------------------------------------------------------------ */
export { UNIT, BG_GRID, TRI_H, BUTTON_UNIT, RB_W, RB_H, RB_S, SHAPE_PATHS, SHAPE_PATHS_BUTTON, SHAPE_BOUNDS, SHAPE_VERTICES, SNAP_THRESHOLD, OUTLINE_VERTICES, OUTLINE_SVG_DIMS, } from './patternShapesConstants';
import { UNIT, SHAPE_PATHS, SHAPE_BOUNDS, SHAPE_VERTICES, } from './patternShapesConstants';
import { computePatternShapeSnap, computeGroupSnapDelta } from './patternShapesSnapHelper';
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
/**
 * Compute offset (dilated) polygon vertices by pushing each edge outward
 * by `offset` SVG units along its outward normal, then intersecting
 * adjacent offset edges. Works for any convex polygon.
 * Automatically detects CW/CCW winding. Applies miter limiting at sharp
 * vertices so the outline doesn't extend excessively at acute angles.
 */
function computeOffsetVertices(verts, offset) {
    var n = verts.length;
    if (n < 3)
        return [];
    // Detect winding via signed area (shoelace). In screen coords (Y-down):
    // positive = CW, negative = CCW. Our normals assume CW, so negate for CCW.
    var signedArea2 = 0;
    for (var i = 0; i < n; i++) {
        var a = verts[i];
        var b = verts[(i + 1) % n];
        signedArea2 += a.x * b.y - b.x * a.y;
    }
    var effectiveOffset = signedArea2 >= 0 ? offset : -offset;
    // Max distance an offset vertex may be from its original vertex (miter limit).
    var MITER_LIMIT = 3;
    var maxDist = Math.abs(offset) * MITER_LIMIT;
    // 1. Compute offset edges
    var offsetEdges = [];
    for (var i = 0; i < n; i++) {
        var a = verts[i];
        var b = verts[(i + 1) % n];
        var dx = b.x - a.x;
        var dy = b.y - a.y;
        var len = Math.hypot(dx, dy);
        var nx = (dy / len) * effectiveOffset;
        var ny = (-dx / len) * effectiveOffset;
        offsetEdges.push({
            p1: { x: a.x + nx, y: a.y + ny },
            p2: { x: b.x + nx, y: b.y + ny },
        });
    }
    // 2. Intersect adjacent offset edges, with miter clamping
    var newVerts = [];
    for (var i = 0; i < n; i++) {
        var prev = offsetEdges[(i - 1 + n) % n];
        var curr = offsetEdges[i];
        var d1x = prev.p2.x - prev.p1.x, d1y = prev.p2.y - prev.p1.y;
        var d2x = curr.p2.x - curr.p1.x, d2y = curr.p2.y - curr.p1.y;
        var cross = d1x * d2y - d1y * d2x;
        var pt = void 0;
        if (Math.abs(cross) < 1e-10) {
            pt = curr.p1;
        }
        else {
            var t = ((curr.p1.x - prev.p1.x) * d2y - (curr.p1.y - prev.p1.y) * d2x) / cross;
            pt = { x: prev.p1.x + t * d1x, y: prev.p1.y + t * d1y };
        }
        var orig = verts[i];
        var dist = Math.hypot(pt.x - orig.x, pt.y - orig.y);
        if (dist > maxDist) {
            var ratio = maxDist / dist;
            pt = { x: orig.x + (pt.x - orig.x) * ratio, y: orig.y + (pt.y - orig.y) * ratio };
        }
        newVerts.push(pt);
    }
    return newVerts;
}
/** Convert vertex array to an SVG path string. */
function vertsToPath(verts) {
    if (verts.length === 0)
        return '';
    return verts.map(function (v, i) { return "".concat(i === 0 ? 'M' : 'L', " ").concat(v.x, " ").concat(v.y); }).join(' ') + ' Z';
}
/**
 * Returns the nearest point on line segment AB to point P, plus the distance.
 * Used for edge-projection snapping (snap shape vertices to outline edges).
 */
export function nearestPointOnSegment(px, py, ax, ay, bx, by) {
    var dx = bx - ax;
    var dy = by - ay;
    var lenSq = dx * dx + dy * dy;
    if (lenSq === 0)
        return { x: ax, y: ay, dist: Math.hypot(px - ax, py - ay) };
    var t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
    var nx = ax + t * dx;
    var ny = ay + t * dy;
    return { x: nx, y: ny, dist: Math.hypot(px - nx, py - ny) };
}
/**
 * Returns edge midpoints for a list of vertices (used for edge-to-edge snapping).
 * Kept separate from vertices so vertex-to-vertex snapping can be prioritized.
 */
export function getMidpoints(verts) {
    if (verts.length < 2)
        return [];
    var mids = [];
    for (var i = 0; i < verts.length; i++) {
        var a = verts[i];
        var b = verts[(i + 1) % verts.length];
        mids.push({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    }
    return mids;
}
/**
 * Transform local shape vertices to canvas coordinates, accounting for
 * CSS transform: rotate(deg) scale(s) with transformOrigin at the wrapper center.
 * The wrapper center matches (shapeBounds.w/2, shapeBounds.h/2) in wrapper coords.
 */
export function getCanvasVertices(localVerts, position, rotation, scale, forShapeType) {
    var bounds = SHAPE_BOUNDS[forShapeType] || { x: 0, y: 0, w: UNIT, h: UNIT };
    // CSS transformOrigin is center of wrapper div (bounds.w/2, bounds.h/2)
    var originX = bounds.w / 2;
    var originY = bounds.h / 2;
    var rad = (rotation * Math.PI) / 180;
    var cos = Math.cos(rad);
    var sin = Math.sin(rad);
    return localVerts.map(function (v) {
        // Convert SVG-local coords to wrapper coords, then offset from wrapper center
        var dx = (v.x - bounds.x - originX) * scale;
        var dy = (v.y - bounds.y - originY) * scale;
        return {
            x: position.x + originX + dx * cos - dy * sin,
            y: position.y + originY + dx * sin + dy * cos,
        };
    });
}
/* ------------------------------------------------------------------ */
/* COMPONENT */
/* ------------------------------------------------------------------ */
var PatternShapesManipulative = forwardRef(function (props, ref) {
    var id = props.id, shapeType = props.shapeType, center = props.center, rotation = props.rotation, scale = props.scale, color = props.color, lock = props.lock, _a = props.isOutline, isOutline = _a === void 0 ? false : _a, _b = props.isGroup, isGroup = _b === void 0 ? false : _b, path = props.path, groupId = props.groupId, svgW = props.svgW, svgH = props.svgH;
    // Determine if this shape is a static outline (converted via outline button) — non-interactive.
    // Regular groups (from Group button) have groupId but lock.positioning=false — they stay interactive.
    var isInOutlineGroup = Boolean(props.isStaticOutline);
    /* ---------------- i18n ---------------- */
    var t = useTranslation().t;
    /* ---------------- GLOBAL ---------------- */
    var _c = globalStore.useStoreState(function (s) { return s.playerStore; }), zoomLevel = _c.zoomLevel, playerBgPattern = _c.playerBgPattern;
    var _d = useContext(DataContext), selectedTool = _d.selectedTool, manipulative = _d.manipulative, setManipulative = _d.setManipulative, panningEnable = _d.panningEnable, setSelectedTool = _d.setSelectedTool, setContextMenu = _d.setContextMenu, contextMenu = _d.contextMenu;
    // Determine if this shape is part of a multi-selection (group overlay handles interaction)
    var isMultiSelected = selectedTool.length > 1 && selectedTool.includes(id);
    // Determine if this shape's group is currently selected (group overlay handles interaction)
    var isGroupSelected = Boolean(groupId) && selectedTool.includes(groupId);
    // True when ANY group overlay is currently active (multi-select or a manipulativeGroup_ selected).
    // When this is true, all individual shapes yield Tab focus to the group overlay.
    // Only the first shape per group gets tabIndex=0; others are skipped by Tab to avoid racing focus timers
    /* ---------------- STATE ---------------- */
    var _f = useState(center), position = _f[0], setPosition = _f[1];
    var _g = useState(rotation), currentRotation = _g[0], setCurrentRotation = _g[1];
    var _h = useState(scale), currentScale = _h[0], setCurrentScale = _h[1];
    var _j = useState(false), isFocused = _j[0], setIsFocused = _j[1];
    var _k = useState(null), dragStart = _k[0], setDragStart = _k[1];
    var _l = useState(false), rotateHandleHover = _l[0], setRotateHandleHover = _l[1];
    // Screen reader announcement text (driven by aria-live region)
    var _m = useState(''), announceText = _m[0], setAnnounceText = _m[1];
    // Duplicate / creation announce — temporarily overrides aria-label (same pattern as Dice)
    var duplicateLabel = useDuplicateAnnounce(id, t('text.patternShape'));
    /* ---------------- REFS ---------------- */
    var posRef = useRef(position);
    var currentRotationRef = useRef(currentRotation);
    currentRotationRef.current = currentRotation;
    var currentScaleRef = useRef(currentScale);
    currentScaleRef.current = currentScale;
    var manipulativeRef = useRef(manipulative);
    manipulativeRef.current = manipulative;
    var shapeNumber = useMemo(function () {
        var sameTypeColor = manipulative.filter(function (m) {
            var d = m.data;
            return d.type === 'patternShapes' && d.shapeType === shapeType && d.color === color;
        });
        return sameTypeColor.findIndex(function (m) { return m.data.id === id; }) + 1;
    }, [manipulative, id, shapeType, color]);
    var dragStartRef = useRef(null);
    var hasMoved = useRef(false);
    // store click modifier keys for selection on tap/click
    var clickModifiersRef = useRef(null);
    // snapshot of all group members' centers at drag start (for group drag)
    var groupDragSnapshotRef = useRef({});
    var _o = useMultiSelectDragSource(id, selectedTool), startMultiDrag = _o.startMultiDrag, moveMultiDrag = _o.moveMultiDrag, endMultiDrag = _o.endMultiDrag;
    var multiDragStartPosRef = useRef(null);
    var groupDragRafRef = useRef(0);
    // ref to wrapper DOM element to compute screen coordinates for rotation center
    var wrapperRef = useRef(null);
    // Rotation handle: press the rotate handle and drag to rotate the shape around its center
    var rotatingRef = useRef(false);
    var rotateCenterRef = useRef(null);
    var rotateStartAngleRef = useRef(0);
    var rotateInitialRotationRef = useRef(0);
    var rotateOldRef = useRef(null);
    var latestRotRef = useRef(0); // tracks latest rotation during drag (avoids stale closure)
    var rotateHasMovedRef = useRef(false); // play audio only on actual rotation movement
    // Overlay button ref — keyboard/screen-reader entry point
    var buttonRef = useRef(null);
    // Keyboard movement: track start/current pos for batched undo + pointer-down guard for onFocus
    var keyMoveSaveTimer = useRef(undefined);
    var keyMoveStartRef = useRef(null);
    var keyMoveCurPosRef = useRef(null);
    var pointerDownRef = useRef(false);
    var rotateHandleFocusedRef = useRef(false);
    // Cancellable timer for auto-focus on programmatic selection (e.g. after duplicate).
    // Must be cancelable so Shift+Tab → body doesn't get hijacked back to this shape.
    var autoFocusTimerRef = useRef(undefined);
    // Rotate handle ref — mouse/touch drag target.
    var rotateHandleRef = useRef(null);
    var startHandleRotate = function (clientX, clientY) {
        var _a;
        if (lock.positioning)
            return;
        rotatingRef.current = true;
        rotateHasMovedRef.current = false;
        rotateOldRef.current = currentRotation;
        // compute center in screen coordinates from the wrapper DOM rect (more robust than math)
        var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        var centerScreen = rect
            ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
            : { x: (position.x + UNIT / 2) * zoomLevel, y: (position.y + UNIT / 2) * zoomLevel };
        rotateCenterRef.current = centerScreen;
        var a = (Math.atan2(clientY - centerScreen.y, clientX - centerScreen.x) * 180) / Math.PI;
        rotateStartAngleRef.current = a;
        rotateInitialRotationRef.current = currentRotation;
        // show grabbing cursor globally while rotating
        document.body.style.cursor = 'grabbing';
        window.addEventListener('mousemove', handleHandleRotateMove);
        window.addEventListener('mouseup', handleHandleRotateEnd);
        window.addEventListener('touchmove', handleHandleRotateMove, { passive: false });
        window.addEventListener('touchend', handleHandleRotateEnd);
        window.addEventListener('touchcancel', handleHandleRotateEnd);
    };
    var handleHandleRotateMove = function (e) {
        if (!rotatingRef.current)
            return;
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        var center = rotateCenterRef.current;
        if (!center)
            return;
        var a = (Math.atan2(clientY - center.y, clientX - center.x) * 180) / Math.PI;
        var delta = a - rotateStartAngleRef.current;
        var newRot = rotateInitialRotationRef.current + delta;
        if (Math.abs(delta) > 1) {
            rotateHasMovedRef.current = true;
        }
        latestRotRef.current = newRot;
        setCurrentRotation(newRot);
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { rotation: newRot }) }) : item;
            });
        });
    };
    var handleHandleRotateEnd = function () {
        var _a, _b;
        if (!rotatingRef.current)
            return;
        var SNAP_THRESHOLD = 8;
        var rawRot = latestRotRef.current;
        // ── Helper: unique edge angles (at rotation=0) for a shape type ──
        var getBaseEdgeAngles = function (st) {
            var verts = SHAPE_VERTICES[st];
            if (!verts || verts.length < 2)
                return [];
            var seen = new Set();
            var result = [];
            for (var i = 0; i < verts.length; i++) {
                var a = verts[i];
                var b = verts[(i + 1) % verts.length];
                var ang = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
                ang = ((ang % 180) + 180) % 180;
                var key = Math.round(ang * 10);
                if (!seen.has(key)) {
                    seen.add(key);
                    result.push(ang);
                }
            }
            return result;
        };
        // ── Neighbour-edge snap: find rotation where any side of this shape aligns
        //    with any side of any other shape on canvas ──
        var myBaseAngles = getBaseEdgeAngles(shapeType);
        var bestDist = Infinity;
        var bestSnap = rawRot;
        manipulative.forEach(function (item) {
            var _a;
            if (item.type !== 'patternShapes')
                return;
            var d = item.data;
            if (d.id === id || d.isOutline || d.groupId)
                return;
            var neighborRot = (_a = d.rotation) !== null && _a !== void 0 ? _a : 0;
            getBaseEdgeAngles(d.shapeType).forEach(function (baseNeighbor) {
                var neighborAngle = (((baseNeighbor + neighborRot) % 180) + 180) % 180;
                myBaseAngles.forEach(function (myBase) {
                    // candidate R: (myBase + R) ≡ neighborAngle  (mod 180)
                    var base = neighborAngle - myBase;
                    var k = Math.round((rawRot - base) / 180);
                    var candidate = base + k * 180;
                    var dist = Math.abs(rawRot - candidate);
                    if (dist < bestDist) {
                        bestDist = dist;
                        bestSnap = candidate;
                    }
                });
            });
        });
        var INTERVAL = shapeType === 'rhombus_white' ? 60 : 30;
        var OFFSET = shapeType === 'rhombus_white' ? 45 : 0;
        var fixedNearest = Math.round((rawRot - OFFSET) / INTERVAL) * INTERVAL + OFFSET;
        // ── Fallback: fixed-interval snap when no neighbour found ──
        if (bestDist > SNAP_THRESHOLD) {
            if (Math.abs(rawRot - fixedNearest) <= SNAP_THRESHOLD) {
                bestDist = Math.abs(rawRot - fixedNearest);
                bestSnap = fixedNearest;
            }
        }
        else if (Math.abs(bestSnap - fixedNearest) <= 2) {
            // Neighbour snap is within 2° of a clean fixed angle — collapse to fixed to prevent oscillation
            bestSnap = fixedNearest;
        }
        // Round to 1 decimal place to eliminate floating-point residue (e.g. -30.00072 → -30.0)
        var snapped = bestDist <= SNAP_THRESHOLD ? Math.round(bestSnap * 10) / 10 : rawRot;
        var rotationSnapped = snapped !== rawRot;
        if (rotationSnapped) {
            playAudio('PatternAdd');
            setCurrentRotation(snapped);
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { rotation: snapped }) }) : item;
                });
            });
            // Re-snap position using the new rotation angle — exclude grid (pass 'none') so
            // only shape-to-shape and outline snaps fire; grid can pull a shape far after rotation.
            var currentPos = (_a = posRef.current) !== null && _a !== void 0 ? _a : position;
            var snappedPos_1 = computePatternShapeSnap(currentPos.x, currentPos.y, shapeType, snapped, currentScale || 1, [id], manipulative, 'none');
            // Apply position re-snap if any snap candidate was found (nearest-distance wins).
            if (snappedPos_1.snapScore > 0 &&
                (snappedPos_1.x !== currentPos.x || snappedPos_1.y !== currentPos.y)) {
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: snappedPos_1 }) }) : item;
                    });
                });
            }
        }
        var old = (_b = rotateOldRef.current) !== null && _b !== void 0 ? _b : 0;
        saveState({
            type: id,
            id: id.split('_')[1],
            propertyName: 'rotation',
            old: JSON.stringify(old),
            new: JSON.stringify(snapped),
        });
        rotatingRef.current = false;
        // restore cursor
        document.body.style.cursor = '';
        window.removeEventListener('mousemove', handleHandleRotateMove);
        window.removeEventListener('mouseup', handleHandleRotateEnd);
        window.removeEventListener('touchmove', handleHandleRotateMove);
        window.removeEventListener('touchend', handleHandleRotateEnd);
        window.removeEventListener('touchcancel', handleHandleRotateEnd);
    };
    var onHandleMouseDown = function (e) {
        e.preventDefault();
        e.stopPropagation();
        startHandleRotate(e.clientX, e.clientY);
    };
    var onHandleTouchStart = function (e) {
        e.preventDefault();
        e.stopPropagation();
        startHandleRotate(e.touches[0].clientX, e.touches[0].clientY);
    };
    /* ---------------- SYNC ---------------- */
    useEffect(function () { return setPosition(center); }, [center]);
    useEffect(function () { return setCurrentRotation(rotation); }, [rotation]);
    useEffect(function () { return setCurrentScale(scale); }, [scale]);
    // keep a ref copy of position for use at drag end
    useEffect(function () {
        posRef.current = position;
    }, [position]);
    var prevSelectedToolRef = useRef([]);
    useEffect(function () {
        var wasFocused = prevSelectedToolRef.current.includes(id);
        var nowSelected = selectedTool.includes(id);
        prevSelectedToolRef.current = selectedTool;
        if (isInOutlineGroup) {
            setIsFocused(false);
        }
        else {
            var isMulti = selectedTool.length > 1 && nowSelected;
            var nowFocused = nowSelected && !isMulti;
            setIsFocused(nowFocused);
            // Auto-focus the overlay button when newly selected programmatically (e.g. after duplicate) — mirrors Dice
            if (nowFocused && !wasFocused && buttonRef.current && !contextMenu) {
                clearTimeout(autoFocusTimerRef.current);
                autoFocusTimerRef.current = window.setTimeout(function () {
                    var _a;
                    autoFocusTimerRef.current = undefined;
                    // Don't steal focus if the user has already moved focus elsewhere (e.g. via Tab/Shift+Tab)
                    var active = document.activeElement;
                    if (active && active !== document.body && active !== buttonRef.current)
                        return;
                    (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }, 100);
            }
        }
    }, [selectedTool, id, isInOutlineGroup]);
    /* ------------------------------------------------------------------ */
    /* KEYBOARD ACCESSIBILITY */
    /* ------------------------------------------------------------------ */
    var handleKeyDown = function (e) {
        var _a, _b, _c, _d, _f, _g, _h, _j;
        if (panningEnable)
            return;
        // Context menu — Enter / Space / Shift+F10 / ContextMenu key (matches Dice pattern).
        // Allowed for all shapes including outlines so Clear Outlines / Remove are reachable.
        if (e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            if (!selectedTool.includes(id)) {
                setSelectedTool([groupId || id]);
            }
            var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect) {
                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: groupId || id });
            }
            return;
        }
        // Movement / rotation / delete — interactive shapes only. Outlines are locked.
        if (isOutline || isInOutlineGroup)
            return;
        // Arrow key movement (10px, 50px with Shift, scaled by zoom)
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
            e.preventDefault();
            if (lock.positioning)
                return;
            var step = e.shiftKey ? 50 : 10;
            var scaledStep = step / zoomLevel;
            var deltas = {
                ArrowUp: { x: 0, y: -scaledStep },
                ArrowDown: { x: 0, y: scaledStep },
                ArrowLeft: { x: -scaledStep, y: 0 },
                ArrowRight: { x: scaledStep, y: 0 },
            };
            var delta = deltas[e.key];
            // Group movement: move all group members by the same delta
            var groupMembers_1 = groupId
                ? manipulativeRef.current.filter(function (m) { return m.data.groupId === groupId; })
                : [];
            var isGroupMove = groupMembers_1.length > 1;
            if (isGroupMove) {
                // Capture per-member start positions on first keypress
                if (!keyMoveStartRef.current) {
                    keyMoveStartRef.current = __assign({}, position);
                    var startMap_1 = {};
                    groupMembers_1.forEach(function (m) {
                        startMap_1[m.data.id] = __assign({}, m.data.center);
                    });
                    keyMoveStartRef.groupMap = startMap_1;
                }
                var startMap_2 = (_b = keyMoveStartRef.groupMap) !== null && _b !== void 0 ? _b : {};
                var curPos_1 = (_c = keyMoveCurPosRef.current) !== null && _c !== void 0 ? _c : position;
                var newCenter_1 = { x: curPos_1.x + delta.x, y: curPos_1.y + delta.y };
                keyMoveCurPosRef.current = newCenter_1;
                var accDx_1 = newCenter_1.x - ((_f = (_d = startMap_2[id]) === null || _d === void 0 ? void 0 : _d.x) !== null && _f !== void 0 ? _f : position.x);
                var accDy_1 = newCenter_1.y - ((_h = (_g = startMap_2[id]) === null || _g === void 0 ? void 0 : _g.y) !== null && _h !== void 0 ? _h : position.y);
                setPosition(newCenter_1);
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        var _a;
                        var start = startMap_2[(_a = item.data.id) !== null && _a !== void 0 ? _a : ''];
                        if (!start)
                            return item;
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: start.x + accDx_1, y: start.y + accDy_1 } }) });
                    });
                });
                if (keyMoveSaveTimer.current)
                    clearTimeout(keyMoveSaveTimer.current);
                keyMoveSaveTimer.current = window.setTimeout(function () {
                    var _a, _b, _c, _d, _f;
                    var oldMap = (_a = keyMoveStartRef.groupMap) !== null && _a !== void 0 ? _a : {};
                    var rawPos = keyMoveCurPosRef.current;
                    if (!rawPos)
                        return;
                    var finalDx = rawPos.x - ((_c = (_b = oldMap[id]) === null || _b === void 0 ? void 0 : _b.x) !== null && _c !== void 0 ? _c : position.x);
                    var finalDy = rawPos.y - ((_f = (_d = oldMap[id]) === null || _d === void 0 ? void 0 : _d.y) !== null && _f !== void 0 ? _f : position.y);
                    var undoEntries = groupMembers_1.map(function (m) {
                        var _a, _b, _c, _d, _f;
                        return ({
                            type: m.data.id,
                            id: m.data.id.split('_')[1],
                            propertyName: 'center',
                            old: JSON.stringify((_a = oldMap[m.data.id]) !== null && _a !== void 0 ? _a : m.data.center),
                            new: JSON.stringify({
                                x: ((_c = (_b = oldMap[m.data.id]) === null || _b === void 0 ? void 0 : _b.x) !== null && _c !== void 0 ? _c : m.data.center.x) + finalDx,
                                y: ((_f = (_d = oldMap[m.data.id]) === null || _d === void 0 ? void 0 : _d.y) !== null && _f !== void 0 ? _f : m.data.center.y) + finalDy,
                            }),
                        });
                    });
                    saveState(undoEntries);
                    var dx = Math.round(finalDx);
                    var dy = Math.round(finalDy);
                    var dirs = [];
                    if (dy < 0)
                        dirs.push("".concat(t('text.up'), " ").concat(Math.abs(dy)));
                    if (dy > 0)
                        dirs.push("".concat(t('text.down'), " ").concat(dy));
                    if (dx < 0)
                        dirs.push("".concat(t('text.left'), " ").concat(Math.abs(dx)));
                    if (dx > 0)
                        dirs.push("".concat(t('text.right'), " ").concat(dx));
                    setAnnounceText('');
                    requestAnimationFrame(function () {
                        return setAnnounceText(t('text.manipulativeMoved', {
                            name: t('text.patternShape'),
                            direction: dirs.join(" ".concat(t('text.and'), " ")),
                            x: Math.round(rawPos.x),
                            y: Math.round(rawPos.y),
                        }));
                    });
                    keyMoveStartRef.current = null;
                    keyMoveStartRef.groupMap = undefined;
                    keyMoveCurPosRef.current = null;
                }, 500);
                return;
            }
            // Solo movement
            var curPos = (_j = keyMoveCurPosRef.current) !== null && _j !== void 0 ? _j : position;
            if (!keyMoveStartRef.current)
                keyMoveStartRef.current = __assign({}, curPos);
            var newCenter_2 = { x: curPos.x + delta.x, y: curPos.y + delta.y };
            keyMoveCurPosRef.current = newCenter_2;
            setPosition(newCenter_2);
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter_2 }) }) : item;
                });
            });
            // Debounced undo + snap + announce (500ms after last keypress)
            if (keyMoveSaveTimer.current)
                clearTimeout(keyMoveSaveTimer.current);
            keyMoveSaveTimer.current = window.setTimeout(function () {
                var oldPos = keyMoveStartRef.current;
                var rawPos = keyMoveCurPosRef.current;
                if (!oldPos || !rawPos)
                    return;
                // Run snap on final position — mirrors context menu snap behaviour
                var totalDx = rawPos.x - oldPos.x;
                var totalDy = rawPos.y - oldPos.y;
                var _a = computeGroupSnapDelta([{ id: id, shapeType: shapeType, center: rawPos, rotation: currentRotationRef.current, scale: currentScaleRef.current || 1 }], [id], manipulativeRef.current, playerBgPattern !== null && playerBgPattern !== void 0 ? playerBgPattern : 'none'), snapDx = _a.dx, snapDy = _a.dy;
                var finalSnapDx = totalDx !== 0 && Math.sign(snapDx) !== 0 && Math.sign(totalDx + snapDx) !== Math.sign(totalDx) ? 0 : snapDx;
                var finalSnapDy = totalDy !== 0 && Math.sign(snapDy) !== 0 && Math.sign(totalDy + snapDy) !== Math.sign(totalDy) ? 0 : snapDy;
                var didSnap = finalSnapDx !== 0 || finalSnapDy !== 0;
                var finalPos = didSnap ? { x: rawPos.x + finalSnapDx, y: rawPos.y + finalSnapDy } : rawPos;
                if (didSnap) {
                    playAudio('PatternAdd');
                    setPosition(finalPos);
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: finalPos }) }) : item;
                        });
                    });
                }
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'center',
                    old: JSON.stringify(oldPos),
                    new: JSON.stringify(finalPos),
                });
                var dx = Math.round(finalPos.x - oldPos.x);
                var dy = Math.round(finalPos.y - oldPos.y);
                var dirs = [];
                if (dy < 0)
                    dirs.push("".concat(t('text.up'), " ").concat(Math.abs(dy)));
                if (dy > 0)
                    dirs.push("".concat(t('text.down'), " ").concat(dy));
                if (dx < 0)
                    dirs.push("".concat(t('text.left'), " ").concat(Math.abs(dx)));
                if (dx > 0)
                    dirs.push("".concat(t('text.right'), " ").concat(dx));
                if (didSnap) {
                    setAnnounceText('');
                    requestAnimationFrame(function () { return setAnnounceText(t('text.patternShapeSnapped')); });
                }
                else {
                    setAnnounceText('');
                    requestAnimationFrame(function () {
                        return setAnnounceText(t('text.manipulativeMoved', {
                            name: t('text.patternShape'),
                            direction: dirs.join(" ".concat(t('text.and'), " ")),
                            x: Math.round(finalPos.x),
                            y: Math.round(finalPos.y),
                        }));
                    });
                }
                keyMoveStartRef.current = null;
                keyMoveCurPosRef.current = null;
            }, 500);
            return;
        }
        // Delete / Backspace
        if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            if (lock.positioning)
                return;
            customEventHandler.dispatchEvent('bottomPanel', { id: 'delete', panel: 'bottomPanel' });
        }
    };
    // Rotation handle keyboard handler — mirrors ProtractorManipulative.tsx:851–862
    var handleRotateKeyDown = function (e) {
        var _a;
        // Escape — return focus to the shape overlay button
        if (e.key === 'Escape') {
            e.preventDefault();
            (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            return;
        }
        if (!['ArrowRight', 'ArrowUp', 'ArrowLeft', 'ArrowDown'].includes(e.key))
            return;
        if (lock.editing)
            return;
        e.preventDefault();
        e.stopPropagation(); // prevent wrapper arrow-key handler from also firing
        var isClockwise = e.key === 'ArrowRight' || e.key === 'ArrowUp';
        var step = e.shiftKey ? 15 : 1;
        var delta = isClockwise ? step : -step;
        var oldRot = currentRotationRef.current;
        var newRot = oldRot + delta;
        saveState({
            type: id,
            id: id.split('_')[1],
            propertyName: 'rotation',
            old: String(oldRot),
            new: String(newRot),
        });
        setCurrentRotation(newRot);
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { rotation: newRot }) }) : item;
            });
        });
        var direction = isClockwise ? t('text.clockwise') : t('text.anticlockwise');
        var baseline = ((newRot % 360) + 360) % 360;
        setAnnounceText('');
        requestAnimationFrame(function () {
            return setAnnounceText(t('text.patternShapeRotated', { degrees: step, direction: direction, baseline: Math.round(baseline) }));
        });
    };
    /* ------------------------------------------------------------------ */
    /* DRAG HANDLING */
    /* ------------------------------------------------------------------ */
    var onMouseDown = function (e) {
        // Completely block interaction for shapes inside an outline group
        // (isStaticOutline shapes are locked and handled below — fall through to lock path)
        // Multi-selected standalone OR group member inside a multi-group-select:
        // SelectionGroup owns the drag for all shapes. SVG pointer-events:auto overrides the
        // wrapper's pointer-events:none, so we must guard explicitly here.
        // Without this guard, grouped shapes would call setSelectedTool([groupId]) and destroy
        // the multi-select, causing the outer bounding box to jump.
        if (isMultiSelected)
            return;
        if (groupId && selectedTool.includes(groupId) && selectedTool.length > 1)
            return;
        e.preventDefault();
        e.stopPropagation();
        // Guard onFocus from double-selecting when mouse triggers both onMouseDown and onFocus
        pointerDownRef.current = true;
        // Outline shapes: select only — no drag or rotate
        if (isOutline) {
            var modifiers = {
                ctrlKey: 'ctrlKey' in e ? e.ctrlKey : false,
                metaKey: 'metaKey' in e ? e.metaKey : false,
                shiftKey: 'shiftKey' in e ? e.shiftKey : false,
            };
            var fakeEvt_1 = {
                ctrlKey: modifiers.ctrlKey,
                metaKey: modifiers.metaKey,
                shiftKey: modifiers.shiftKey,
            };
            setSelectedTool(function (prev) { return selectTool(fakeEvt_1, id, prev, false); });
            return;
        }
        // For groups (including locked groups): select the group but DON'T start a drag.
        // Must run before the lock check so locked groups can still be selected.
        if (groupId) {
            var modifiers = {
                ctrlKey: 'ctrlKey' in e ? e.ctrlKey : false,
                metaKey: 'metaKey' in e ? e.metaKey : false,
                shiftKey: 'shiftKey' in e ? e.shiftKey : false,
            };
            var fakeEvt_2 = {
                ctrlKey: modifiers.ctrlKey,
                metaKey: modifiers.metaKey,
                shiftKey: modifiers.shiftKey,
            };
            setSelectedTool(function (prev) { return selectTool(fakeEvt_2, groupId, prev, false); });
            dragStartRef.current = null;
            hasMoved.current = false;
            return;
        }
        if (panningEnable)
            return;
        var isModifierClick = ('ctrlKey' in e && e.ctrlKey) ||
            ('metaKey' in e && e.metaKey) ||
            ('shiftKey' in e && e.shiftKey);
        // For locked shapes: still register dragStart so stopDrag fires on mouseup
        // and selection logic runs — but movement is blocked inside handleMove.
        if (lock.positioning) {
            if (isModifierClick) {
                setSelectedTool(function (prev) {
                    return selectTool({
                        ctrlKey: ('ctrlKey' in e && e.ctrlKey) || false,
                        metaKey: ('metaKey' in e && e.metaKey) || false,
                        shiftKey: ('shiftKey' in e && e.shiftKey) || false,
                    }, id, prev, false);
                });
                return;
            }
            clickModifiersRef.current = {
                ctrlKey: 'ctrlKey' in e ? e.ctrlKey : false,
                metaKey: 'metaKey' in e ? e.metaKey : false,
                shiftKey: 'shiftKey' in e ? e.shiftKey : false,
            };
            dragStartRef.current = { x: 0, y: 0 };
            hasMoved.current = false;
            setDragStart({ x: 0, y: 0 });
            return;
        }
        // Modifier+click on a standalone unlocked shape: apply selection immediately.
        // Registering mouseup via useEffect risks missing it if the event fires before
        // the effect runs — the common case for fast shift/cmd+clicks.
        if (isModifierClick) {
            setSelectedTool(function (prev) {
                return selectTool({
                    ctrlKey: ('ctrlKey' in e && e.ctrlKey) || false,
                    metaKey: ('metaKey' in e && e.metaKey) || false,
                    shiftKey: ('shiftKey' in e && e.shiftKey) || false,
                }, id, prev, false);
            });
            return;
        }
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        dragStartRef.current = { x: clientX, y: clientY };
        hasMoved.current = false;
        // capture modifier keys for selection behaviour (ctrl/meta for multi-select)
        clickModifiersRef.current = {
            ctrlKey: 'ctrlKey' in e ? e.ctrlKey : false,
            metaKey: 'metaKey' in e ? e.metaKey : false,
            shiftKey: 'shiftKey' in e ? e.shiftKey : false,
        };
        setDragStart({
            x: clientX - position.x * zoomLevel,
            y: clientY - position.y * zoomLevel,
        });
        multiDragStartPosRef.current = { x: position.x, y: position.y };
        startMultiDrag();
    };
    useEffect(function () {
        // ── Snap helper: delegates to shared patternShapesSnapHelper ──────────
        var computeSnappedPosition = function (rawX, rawY) {
            return computePatternShapeSnap(rawX, rawY, shapeType, currentRotation, currentScale || 1, [id], manipulative, playerBgPattern !== null && playerBgPattern !== void 0 ? playerBgPattern : 'none');
        };
        var handleMove = function (e) {
            var _a, _b;
            if (!dragStartRef.current || lock.positioning || isInOutlineGroup)
                return;
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var dx = clientX - dragStartRef.current.x;
            var dy = clientY - dragStartRef.current.y;
            if (Math.hypot(dx, dy) > 4) {
                if (!hasMoved.current)
                    hasMoved.current = true;
            }
            if (!hasMoved.current)
                return;
            // Group drag: move ALL group members by the same delta (throttled to 1 update per frame)
            if (groupId && !lock.positioning) {
                var dxCanvas_1 = dx / zoomLevel;
                var dyCanvas_1 = dy / zoomLevel;
                // Update this shape's local position immediately for visual feedback
                var mySnap = groupDragSnapshotRef.current[id];
                if (mySnap)
                    setPosition({ x: mySnap.x + dxCanvas_1, y: mySnap.y + dyCanvas_1 });
                // Throttle context update for other group members via RAF
                if (groupDragRafRef.current)
                    cancelAnimationFrame(groupDragRafRef.current);
                groupDragRafRef.current = requestAnimationFrame(function () {
                    groupDragRafRef.current = 0;
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            var snap = groupDragSnapshotRef.current[(_a = item.data.id) !== null && _a !== void 0 ? _a : ''];
                            if (!snap)
                                return item;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: snap.x + dxCanvas_1, y: snap.y + dyCanvas_1 } }) });
                        });
                    });
                });
                return;
            }
            // Free movement — only update local position during drag for visual feedback.
            // Context (setManipulative) is synced once on drop in stopDrag to avoid
            // re-rendering all widgets on every frame.
            var newX = (clientX - ((_a = dragStart === null || dragStart === void 0 ? void 0 : dragStart.x) !== null && _a !== void 0 ? _a : 0)) / zoomLevel;
            var newY = (clientY - ((_b = dragStart === null || dragStart === void 0 ? void 0 : dragStart.y) !== null && _b !== void 0 ? _b : 0)) / zoomLevel;
            setPosition({ x: newX, y: newY });
            if (multiDragStartPosRef.current) {
                moveMultiDrag(newX - multiDragStartPosRef.current.x, newY - multiDragStartPosRef.current.y);
            }
        };
        var stopDrag = function (_e) {
            var _a, _b, _c;
            if (!hasMoved.current) {
                // Treat as click/tap — set selection. If this shape is part of a group,
                // select the group token instead of the individual member so the user
                // gets a single rotate handler for the group.
                var modifiers = clickModifiersRef.current || {
                    ctrlKey: false,
                    metaKey: false,
                    shiftKey: false,
                };
                var fakeEvt_3 = {
                    ctrlKey: modifiers.ctrlKey,
                    metaKey: modifiers.metaKey,
                    shiftKey: modifiers.shiftKey,
                };
                var thisItem = manipulative.find(function (m) { return m.data.id === id; });
                var targetId_1 = (_a = thisItem === null || thisItem === void 0 ? void 0 : thisItem.data.groupId) !== null && _a !== void 0 ? _a : id;
                setSelectedTool(function (prev) { return selectTool(fakeEvt_3, targetId_1, prev, false); });
            }
            else if (groupId &&
                !lock.positioning &&
                Object.keys(groupDragSnapshotRef.current).length > 0) {
                // Group drag complete: snap this shape, then apply same snap delta to all members
                var rawPos = (_b = posRef.current) !== null && _b !== void 0 ? _b : position;
                var snapped = computeSnappedPosition(rawPos.x, rawPos.y);
                var snapDx_1 = snapped.x - rawPos.x;
                var snapDy_1 = snapped.y - rawPos.y;
                var _newState_1 = manipulative.map(function (item) {
                    var _a;
                    var oldSnap = groupDragSnapshotRef.current[(_a = item.data.id) !== null && _a !== void 0 ? _a : ''];
                    if (!oldSnap)
                        return item;
                    var current = item.data.center;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: current.x + snapDx_1, y: current.y + snapDy_1 } }) });
                });
                // Save undo for each group member
                var groupMemberIds_1 = Object.keys(groupDragSnapshotRef.current);
                Object.entries(groupDragSnapshotRef.current).forEach(function (_a) {
                    var sid = _a[0], oldCenter = _a[1];
                    var newItem = _newState_1.find(function (m) { return m.data.id === sid; });
                    if (newItem) {
                        saveState({
                            type: sid,
                            id: sid.split('_')[1],
                            propertyName: 'center',
                            old: JSON.stringify(oldCenter),
                            new: JSON.stringify(newItem.data.center),
                        });
                    }
                });
                setManipulative(function () { return _newState_1; });
                // Bring the whole cluster to front, preserving relative order among
                // its members. Applied via a functional update against the live
                // `prev` (which will already include the position update above,
                // since React applies queued updates in order) — never a replacement
                // built from a stale ref/snapshot.
                var beforeIds = manipulativeRef.current.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                var orderedGroupIds_1 = manipulativeRef.current
                    .filter(function (m) { return groupMemberIds_1.includes(m.data.id); })
                    .map(function (m) { return m.data.id; });
                var alreadyAtFront = orderedGroupIds_1.length > 0 &&
                    beforeIds.slice(-orderedGroupIds_1.length).join(',') === orderedGroupIds_1.join(',');
                if (orderedGroupIds_1.length > 0 && !alreadyAtFront) {
                    var afterIds = __spreadArray(__spreadArray([], beforeIds.filter(function (bid) { return !groupMemberIds_1.includes(bid); }), true), orderedGroupIds_1, true);
                    saveState({
                        type: 'multipleManipulatives',
                        id: orderedGroupIds_1.join(','),
                        propertyName: 'reorder',
                        old: JSON.stringify(beforeIds),
                        new: JSON.stringify(afterIds),
                    });
                    setManipulative(function (prev) { return bringManipulativesToFront(prev, orderedGroupIds_1); });
                }
                groupDragSnapshotRef.current = {};
                if (snapDx_1 !== 0 || snapDy_1 !== 0)
                    playAudio('PatternAdd');
            }
            else {
                var _oldData = center;
                // Snap the raw drop position to the nearest grid point
                var rawPos = (_c = posRef.current) !== null && _c !== void 0 ? _c : position;
                var _newData_1 = computeSnappedPosition(rawPos.x, rawPos.y);
                if (_newData_1.x !== rawPos.x || _newData_1.y !== rawPos.y)
                    playAudio('PatternAdd');
                // Animate shape to snapped position
                setPosition(_newData_1);
                // Update the manipulative list with the snapped center
                var _newState_2 = manipulative.map(function (item) {
                    if (item.data.id === id) {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: _newData_1 }) });
                    }
                    else
                        return item;
                });
                var batchEntries = [
                    {
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'center',
                        old: JSON.stringify(_oldData),
                        new: JSON.stringify(_newData_1),
                    },
                ];
                setManipulative(function () { return _newState_2; });
                // Bring this shape to front on drop. Applied via a functional update
                // against the live `prev` (which already includes the position
                // update above) — never a replacement built from a stale snapshot.
                var beforeIds = manipulativeRef.current.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                var alreadyAtFront = beforeIds[beforeIds.length - 1] === id;
                if (!alreadyAtFront) {
                    batchEntries.push({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'reorder',
                        old: JSON.stringify(beforeIds),
                        new: JSON.stringify(__spreadArray(__spreadArray([], beforeIds.filter(function (bid) { return bid !== id; }), true), [id], false)),
                    });
                    setManipulative(function (prev) { return bringManipulativesToFront(prev, [id]); });
                }
                if (!registerMultiSelectSaveEntries(id, batchEntries)) {
                    saveState(batchEntries);
                }
            }
            dragStartRef.current = null;
            setDragStart(null);
            hasMoved.current = false;
            multiDragStartPosRef.current = null;
            endMultiDrag();
        };
        if (dragStart && !isGroup && !isInOutlineGroup) {
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('touchmove', handleMove, { passive: false });
        }
        if (dragStart && !isGroup) {
            window.addEventListener('mouseup', stopDrag);
            window.addEventListener('touchend', stopDrag);
            window.addEventListener('touchcancel', stopDrag);
        }
        return function () {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', stopDrag);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', stopDrag);
            window.removeEventListener('touchcancel', stopDrag);
        };
    }, [dragStart, lock.positioning, isGroup, isInOutlineGroup]);
    /* ------------------------------------------------------------------ */
    /* BOTTOM TOOLBAR EVENTS */
    /* ------------------------------------------------------------------ */
    useEffect(function () {
        if ((!isFocused && !isGroupSelected) || lock.editing)
            return;
        var onColorChange = function (data) {
            // Color changes for groups are handled by the group-specific useEffect below
            if (!isFocused)
                return;
            var currentItem = manipulativeRef.current.find(function (m) { return m.data.id === id; });
            if (!currentItem)
                return;
            var _oldColor = currentItem.data.color;
            var _newColor = data.colorKey;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { color: _newColor }) }) : item;
                });
            });
            saveState({
                type: id,
                id: id.split('_')[1],
                propertyName: 'color',
                old: JSON.stringify(_oldColor),
                new: JSON.stringify(_newColor),
            });
        };
        var onToolClick = function (data) {
            var _a, _b;
            // Group rotation is handled by PatternShapesSelectionGroup — skip here
            if (isGroupSelected && (data.id === 'rotateClockwise' || data.id === 'rotateAnticlockwise'))
                return;
            if (data.id === 'rotateLeft' ||
                data.id === 'rotateRight' ||
                data.id === 'rotateClockwise' ||
                data.id === 'rotateAnticlockwise') {
                var delta = data.id === 'rotateLeft' || data.id === 'rotateAnticlockwise' ? -15 : 15;
                var _old = currentRotationRef.current;
                var _new_1 = _old + delta;
                setCurrentRotation(_new_1);
                // Snap position after rotation — vertices shift, may now align with a neighbour
                var currentItem = manipulativeRef.current.find(function (m) { return m.data.id === id; });
                var rawCenter = (_b = (_a = currentItem === null || currentItem === void 0 ? void 0 : currentItem.data) === null || _a === void 0 ? void 0 : _a.center) !== null && _b !== void 0 ? _b : { x: 0, y: 0 };
                var snapped = computePatternShapeSnap(rawCenter.x, rawCenter.y, shapeType, _new_1, scale !== null && scale !== void 0 ? scale : 1, [id], manipulativeRef.current, playerBgPattern !== null && playerBgPattern !== void 0 ? playerBgPattern : 'none');
                var newCenter_3 = { x: snapped.x, y: snapped.y };
                var didSnap_1 = snapped.x !== rawCenter.x || snapped.y !== rawCenter.y;
                if (didSnap_1)
                    playAudio('PatternAdd');
                setManipulative(function (prevM) {
                    return prevM.map(function (item) {
                        return item.data.id === id
                            ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { rotation: _new_1, center: newCenter_3 }) }) : item;
                    });
                });
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'completeData',
                    old: JSON.stringify({ rotation: _old, center: rawCenter }),
                    new: JSON.stringify({ rotation: _new_1, center: newCenter_3 }),
                });
                var isClockwise_1 = delta > 0;
                var baseline_1 = ((_new_1 % 360) + 360) % 360;
                setAnnounceText('');
                requestAnimationFrame(function () {
                    return setAnnounceText(didSnap_1
                        ? t('text.patternShapeRotated', {
                            degrees: 15,
                            direction: isClockwise_1 ? t('text.clockwise') : t('text.anticlockwise'),
                            baseline: Math.round(baseline_1),
                        }) +
                            '. ' +
                            t('text.patternShapeSnappedAt', {
                                name: t('text.patternShape'),
                                x: Math.round(newCenter_3.x),
                                y: Math.round(newCenter_3.y),
                            })
                        : t('text.patternShapeRotated', {
                            degrees: 15,
                            direction: isClockwise_1 ? t('text.clockwise') : t('text.anticlockwise'),
                            baseline: Math.round(baseline_1),
                        }));
                });
            }
            // Scale for groups is handled by GameController — skip here to avoid double-scaling
            var isInAnyGroup = Boolean(groupId) &&
                manipulativeRef.current.filter(function (m) { return m.data.groupId === groupId; }).length > 1;
            if (!isGroupSelected &&
                !isInAnyGroup &&
                (data.id === 'scalePlus' || data.id === 'scaleMinus')) {
                var _old = currentScaleRef.current;
                var _new_2 = data.id === 'scalePlus'
                    ? Math.min(5, Math.round((_old + 0.1) * 10) / 10)
                    : Math.max(0.5, Math.round((_old - 0.1) * 10) / 10);
                setCurrentScale(_new_2);
                setManipulative(function (prevM) {
                    return prevM.map(function (item) {
                        return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: _new_2 }) }) : item;
                    });
                });
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'scale',
                    old: JSON.stringify(_old),
                    new: JSON.stringify(_new_2),
                });
                setAnnounceText('');
                requestAnimationFrame(function () {
                    return setAnnounceText(t('text.patternShapeScaled', {
                        direction: data.id === 'scalePlus' ? t('text.up') : t('text.down'),
                        value: _new_2,
                    }));
                });
            }
        };
        customEventHandler.addEventListener('colourChange', onColorChange);
        customEventHandler.addEventListener('bottomPanel', onToolClick);
        return function () {
            customEventHandler.removeEventListener('colourChange', onColorChange);
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
        };
    }, [isFocused, isGroupSelected, lock.editing]);
    /* ---- Color change for grouped / multi-selected pattern shapes ---- */
    useEffect(function () {
        if (isInOutlineGroup || lock.editing)
            return;
        var isMulti = selectedTool.length > 1 && selectedTool.includes(id);
        var isInSelectedGroup = Boolean(groupId) && selectedTool.includes(groupId);
        if (!isMulti && !isInSelectedGroup)
            return;
        var onColorChange = function (data) {
            var currentItem = manipulativeRef.current.find(function (m) { return m.data.id === id; });
            if (!currentItem)
                return;
            var _oldColor = currentItem.data.color;
            var _newColor = data.colorKey;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { color: _newColor }) }) : item;
                });
            });
            saveState({
                type: id,
                id: id.split('_')[1],
                propertyName: 'color',
                old: JSON.stringify(_oldColor),
                new: JSON.stringify(_newColor),
            });
        };
        customEventHandler.addEventListener('colourChange', onColorChange);
        return function () {
            customEventHandler.removeEventListener('colourChange', onColorChange);
        };
    }, [selectedTool, id, groupId, isInOutlineGroup, lock.editing]);
    /* ------------------------------------------------------------------ */
    /* IMPERATIVE HANDLE */
    /* ------------------------------------------------------------------ */
    useImperativeHandle(ref, function () { return ({
        rotateTo: function (deg) {
            setCurrentRotation(deg);
        },
    }); });
    /* ------------------------------------------------------------------ */
    /* RENDER */
    /* ------------------------------------------------------------------ */
    // For both grouped and standalone shapes we position the whole wrapper element
    // absolutely (left/top set to the incoming `position`). This avoids translating
    // large offsets inside a small svg which can get clipped. Rotation/scale are
    // applied on the wrapper so the SVG content can be simple and centered.
    var transform = useMemo(function () {
        return '';
    }, []);
    // Uniform border styling for all shapes
    var fillColor = ColorEnum[color];
    var borderColor = '#293273';
    // When shape is in an outline group (static), hide the border by matching it to fill
    var strokeColor = isInOutlineGroup ? fillColor : borderColor;
    // Shape bounding box — needed for SVG viewBox, wrapper sizing, and outline offset
    var shapeBounds = SHAPE_BOUNDS[shapeType] || { x: 0, y: 0, w: UNIT, h: UNIT };
    // Selection outline: shape-following outline with uniform gap
    var outlineGapPx = 0; // no gap — outline sits on shape edge, only external glow is visible
    var outlineStrokePx = 1; // outline stroke width (screen px)
    // Convert to SVG units (1 SVG unit = currentScale * zoomLevel screen px)
    var invScaleFactor = 1 / (zoomLevel * (currentScale || 1));
    var outlinePadUnits = outlineGapPx * invScaleFactor;
    var outlineStrokeUnits = outlineStrokePx * invScaleFactor;
    // Pre-compute offset outline vertices (used for both SVG path and handle positioning)
    var outlineVerts = useMemo(function () {
        var verts = SHAPE_VERTICES[shapeType];
        if (!verts || verts.length < 3)
            return null;
        return computeOffsetVertices(verts, outlinePadUnits);
    }, [shapeType, outlinePadUnits]);
    // Y coordinate where a vertical line at the wrapper's horizontal center
    // intersects the outline polygon's topmost edge. This is where the
    // connecting line meets the outline (e.g. the hypotenuse for right triangle).
    var outlineAnchorY = useMemo(function () {
        if (!outlineVerts || outlineVerts.length === 0)
            return -outlinePadUnits;
        // Target X in SVG coords = center of shape bounds
        var targetX = shapeBounds.x + shapeBounds.w / 2;
        var minY = Infinity;
        var n = outlineVerts.length;
        for (var i = 0; i < n; i++) {
            var a = outlineVerts[i];
            var b = outlineVerts[(i + 1) % n];
            // Check if this edge spans targetX horizontally
            if ((a.x <= targetX && b.x >= targetX) || (b.x <= targetX && a.x >= targetX)) {
                var dx = b.x - a.x;
                if (Math.abs(dx) < 1e-10)
                    continue;
                var t_1 = (targetX - a.x) / dx;
                var y = a.y + t_1 * (b.y - a.y);
                if (y < minY)
                    minY = y;
            }
        }
        // Convert from SVG coords to wrapper coords
        return minY === Infinity ? -outlinePadUnits : minY - shapeBounds.y;
    }, [outlineVerts, outlinePadUnits, shapeBounds.x, shapeBounds.y, shapeBounds.w]);
    // When the shape is NOT actively self-dragging/rotating, use prop values directly
    // to eliminate the one-frame useEffect delay during group operations.
    var renderPos = dragStart ? position : center;
    var renderRot = rotatingRef.current ? currentRotation : rotation;
    // For outline library shapes we embed the full canvas dimensions directly in the
    // wrapper so that position.x/y is the true top-left in canvas coordinates.
    var canvasW = isOutline ? (svgW !== null && svgW !== void 0 ? svgW : UNIT * currentScale) : shapeBounds.w;
    var canvasH = isOutline ? (svgH !== null && svgH !== void 0 ? svgH : UNIT * currentScale) : shapeBounds.h;
    return (_jsxs("div", { ref: wrapperRef, className: "patternShapeWrapper", "aria-hidden": groupId || isInOutlineGroup ? true : undefined, "data-manipulative-id": id, style: {
            // Always position absolutely. For grouped shapes `position` holds
            // coordinates local to the group origin; for standalone it is canvas coords.
            position: 'absolute',
            overflow: 'visible',
            top: renderPos.y,
            left: renderPos.x,
            // Outline shapes (library + static converted) always render behind
            // regular shapes, regardless of array position — negative (not 0)
            // so they stay behind both `auto` and `0`-tier siblings unconditionally.
            // Regular shapes get a transient CSS-only boost while actively
            // dragging; permanent stacking comes from `manipulative` array
            // position (reordered + saveState-tracked on drop).
            zIndex: isOutline || isInOutlineGroup ? -1 : dragStart ? 9999 : undefined,
            // Contains this shape's own internal sub-element z-indices (handles,
            // a11y overlay button) to just its own children, so they can't leak
            // out and compete with other manipulatives now that this wrapper no
            // longer always carries an explicit z-index of its own.
            isolation: 'isolate',
            // Smooth snap animation on drop; no transition while actively dragging or group-manipulated
            transition: dragStart || isMultiSelected || isGroupSelected
                ? 'none'
                : 'top 0.12s ease-out, left 0.12s ease-out',
            pointerEvents: 'none',
            cursor: 'default',
            width: canvasW,
            height: canvasH,
            // Outlines with explicit svgW/svgH: dimensions are already baked in — just rotate.
            // Legacy square outlines (no svgW): keep scale(2.3) visual enlargement.
            // Regular shapes: CSS scale from centre.
            transform: isOutline
                ? svgW
                    ? "rotate(".concat(renderRot, "deg)")
                    : "rotate(".concat(renderRot, "deg) scale(2.3)")
                : "rotate(".concat(renderRot, "deg) scale(").concat(currentScale, ")"),
            transformOrigin: "".concat(canvasW / 2, "px ").concat(canvasH / 2, "px"),
            backfaceVisibility: 'hidden',
            willChange: 'transform',
        }, children: [!isInOutlineGroup && (_jsx("button", { ref: buttonRef, "data-ps-id": id, "aria-label": duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : (isOutline
                    ? "".concat(t('text.outline'), " ").concat(t('text.patternShape').toLowerCase(), ". Press Enter for options.")
                    : t('text.patternShapeAriaLabel', {
                        shapeType: t("text.".concat(shapeType), { defaultValue: shapeType }),
                        color: t("text.color_".concat(color), { defaultValue: color }),
                        number: shapeNumber,
                    })), "aria-roledescription": t('text.patternShape'), onFocus: function () {
                    if (pointerDownRef.current) {
                        pointerDownRef.current = false;
                        return;
                    }
                    // Library outlines have no groupId — select the outline itself.
                    // For grouped non-outline shapes: select the GROUP, not the individual shape.
                    var idToSelect = isOutline ? id : (groupId || id);
                    if (!selectedTool.includes(idToSelect)) {
                        // Use direct array (not selectTool) to avoid Shift+Tab multi-select:
                        // selectTool reads _modifierKeys.shift which is true while Shift+Tab is held
                        setSelectedTool([idToSelect]);
                    }
                }, onBlur: function () {
                    clearTimeout(autoFocusTimerRef.current);
                    autoFocusTimerRef.current = undefined;
                }, onKeyDown: handleKeyDown, onClick: IS_TOUCH_DEVICE
                    ? function () {
                        var _a;
                        var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                        if (rect) {
                            setContextMenu({ x: rect.right, y: rect.top, manipulativeId: groupId || id });
                        }
                    }
                    : undefined, tabIndex: groupId ? -1 : 0, className: "patternShapeOverlayBtn", style: {
                    position: 'absolute',
                    inset: 0,
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    pointerEvents: 'none',
                    zIndex: 3,
                } })), !isOutline &&
                !isInOutlineGroup &&
                !lock.positioning &&
                (function () {
                    var handleDistancePx = shapeType === 'triangle_right' ? 40 : 20;
                    var handleSize = 40;
                    var invScale = 1 / (zoomLevel * (currentScale || 1));
                    var handleSizeScaled = handleSize * invScale;
                    var lineWidthUnits = 1 * invScale;
                    var handleDistUnits = handleDistancePx * invScale;
                    var handleCenterX = canvasW / 2;
                    var lineBottomY = outlineAnchorY;
                    var lineTopY = lineBottomY - handleDistUnits;
                    var handleTopY = lineTopY - handleSizeScaled;
                    // Show visually only when focused and not dragging; always keep in DOM for Tab stability
                    var visible = isFocused && dragStart === null;
                    return (_jsxs(_Fragment, { children: [_jsx("div", { "aria-hidden": true, 
                                // Classed so the whiteboard screenshot can drop it with the
                                // handle it belongs to — selection chrome, not saved workspace
                                // (SELECTION_CHROME_CLASSES in WhiteBoard.tsx).
                                className: "patternShapeRotateLine", style: {
                                    position: 'absolute',
                                    top: lineTopY,
                                    left: handleCenterX - lineWidthUnits / 2,
                                    width: lineWidthUnits,
                                    height: Math.abs(lineBottomY - lineTopY),
                                    background: '#000',
                                    borderRadius: lineWidthUnits / 2,
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                    opacity: visible ? 1 : 0,
                                } }), _jsx("button", { ref: rotateHandleRef, className: "patternShapeRotateHandle", role: "slider", "aria-label": t('text.rotate'), "aria-valuemin": -1, "aria-valuemax": 361, "aria-valuenow": Math.round(((currentRotation % 360) + 360) % 360), "aria-hidden": (!isFocused && !rotateHandleFocusedRef.current) || undefined, tabIndex: isFocused && !groupId ? 0 : -1, onFocus: function () { rotateHandleFocusedRef.current = true; }, onBlur: function () { rotateHandleFocusedRef.current = false; }, onMouseDown: visible ? onHandleMouseDown : undefined, onTouchStart: visible ? onHandleTouchStart : undefined, onKeyDown: handleRotateKeyDown, onPointerEnter: function () { return visible && setRotateHandleHover(true); }, onPointerLeave: function () { return setRotateHandleHover(false); }, style: {
                                    position: 'absolute',
                                    top: handleTopY,
                                    left: handleCenterX - handleSizeScaled / 2,
                                    width: handleSize,
                                    height: handleSize,
                                    transform: "scale(".concat(invScale, ")"),
                                    transformOrigin: 'top left',
                                    zIndex: 2,
                                    cursor: visible ? 'grab' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: visible ? 'auto' : 'none',
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    opacity: visible ? 1 : 0,
                                }, children: _jsx("img", { src: getImageSrc(visible && rotateHandleHover
                                        ? 'assets/images/manipulative/patternShape/Rotate_H.svg'
                                        : 'assets/images/manipulative/patternShape/Rotate_N.svg'), alt: "", width: handleSize, height: handleSize, style: {
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        pointerEvents: visible ? 'auto' : 'none',
                                    } }) })] }));
                })(), _jsxs("svg", { "aria-hidden": "true", viewBox: isOutline
                    ? "0 0 ".concat(canvasW, " ").concat(canvasH)
                    : "".concat(shapeBounds.x, " ").concat(shapeBounds.y, " ").concat(shapeBounds.w, " ").concat(shapeBounds.h), width: canvasW, height: canvasH, style: {
                    position: 'relative',
                    zIndex: 0,
                    overflow: 'visible',
                    pointerEvents: 'none',
                    cursor: 'inherit',
                }, children: [(isFocused || isMultiSelected) &&
                        !isInOutlineGroup &&
                        dragStart === null &&
                        outlineVerts &&
                        outlineVerts.length > 0 &&
                        (function () {
                            // Match array model: box-shadow: rgb(80, 99, 174) 0px 0px 20px 0px
                            var glowBlur = 10 * invScaleFactor; // stdDeviation ≈ half of CSS blur (20px/2)
                            var glowStroke = 10 * invScaleFactor;
                            return (_jsxs(_Fragment, { children: [_jsx("defs", { children: _jsx("filter", { id: "outlineGlow_".concat(id), x: "-50%", y: "-50%", width: "200%", height: "200%", children: _jsx("feGaussianBlur", { in: "SourceGraphic", stdDeviation: glowBlur }) }) }), _jsx("path", { d: vertsToPath(outlineVerts), fill: "none", stroke: "rgb(80, 99, 174)", strokeWidth: glowStroke, strokeLinejoin: "round", pointerEvents: "none", filter: "url(#outlineGlow_".concat(id, ")") })] }));
                        })(), (isMultiSelected || isGroupSelected) &&
                        !isOutline &&
                        !isInOutlineGroup &&
                        dragStart === null && (_jsx("circle", { 
                        // Selection-only affordance: white fill + a #293273 stroke, neither
                        // of which the screenshot's colour rules match (they only look for
                        // the #5063AE accent). Named so `SELECTION_CHROME_CLASSES` in
                        // WhiteBoard.tsx can drop it from the capture.
                        className: "psCenterDot", cx: shapeBounds.x + shapeBounds.w / 2, cy: shapeBounds.y + shapeBounds.h / 2, r: 5 * invScaleFactor, fill: "#fff", stroke: "#293273", strokeWidth: 1.5 * invScaleFactor, pointerEvents: "none" })), _jsx("g", { transform: transform, children: shapeType === 'outline' && path && path.includes('.svg') ? (_jsx("image", { href: path, x: "0", y: "0", width: canvasW, height: canvasH, imageRendering: "crispEdges", onMouseDown: onMouseDown, onTouchStart: onMouseDown, style: {
                                pointerEvents: 'auto',
                                cursor: isOutline || isInOutlineGroup ? 'pointer' : dragStart ? 'grabbing' : 'grab',
                            } })) : (_jsxs(_Fragment, { children: [_jsx("defs", { children: _jsx("clipPath", { id: "shapeClip_".concat(id), children: _jsx("path", { d: shapeType === 'outline' && path ? path : SHAPE_PATHS[shapeType] }) }) }), _jsx("path", { d: shapeType === 'outline' && path ? path : SHAPE_PATHS[shapeType], fill: fillColor, stroke: strokeColor, strokeWidth: 2, strokeLinejoin: "round", paintOrder: "stroke fill", onMouseDown: onMouseDown, onTouchStart: onMouseDown, style: {
                                        pointerEvents: 'auto',
                                        cursor: isOutline || isInOutlineGroup ? 'pointer' : dragStart ? 'grabbing' : 'grab',
                                    } }), _jsx("path", { d: shapeType === 'outline' && path ? path : SHAPE_PATHS[shapeType], fill: "none", stroke: strokeColor, strokeWidth: 2, strokeLinejoin: "round", clipPath: "url(#shapeClip_".concat(id, ")") })] })) })] }), isFocused && !isOutline && !isInOutlineGroup && (_jsx("div", { "aria-live": "polite", "aria-atomic": "true", style: {
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                }, children: announceText }))] }));
});
export default PatternShapesManipulative;
